import { useState } from "react";
import { Send, Loader2, Upload, FileSearch, UserCheck, Handshake, Target } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { submitCareer } from "../lib/api";
import { toast } from "sonner";

const HOW_IT_WORKS = [
  {
    icon: Upload,
    t: "Upload your resume",
    d: "Share your CV link (Google Drive / Dropbox) along with your preferred role.",
  },
  {
    icon: FileSearch,
    t: "We screen & shortlist",
    d: "Our placement cell reviews your profile and matches it with the right openings.",
  },
  {
    icon: UserCheck,
    t: "Interview prep & alignment",
    d: "Mock interviews, soft-skills polish, and one-on-one guidance from industry experts.",
  },
  {
    icon: Handshake,
    t: "Get placed",
    d: "We align your interview with relevant employers and stay with you till you get the job.",
  },
];

export default function Careers() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    resume_link: "",
    cover_note: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.position || !form.experience || !form.resume_link) {
      toast.error("Please fill name, email, phone, preferred role, course/experience and resume link.");
      return;
    }
    setLoading(true);
    try {
      await submitCareer(form);
      toast.success("Resume received! Our placement team will screen and reach out soon.");
      setForm({ name: "", email: "", phone: "", position: "", experience: "", resume_link: "", cover_note: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <PageHero
        eyebrow="TASSEI Placement Cell"
        title="From course completion to job in hand."
        desc="Exclusively for TASSEI students & alumni. Upload your resume — our placement team screens your profile, prepares you for interviews and aligns you with the right openings from our hiring partners."
        image="https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920"
        alt="Career consultation and placement"
      />

      {/* HOW IT WORKS */}
      <section className="pt-16 pb-12" data-testid="placement-how-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {HOW_IT_WORKS.map((v, idx) => {
            const Icon = v.icon;
            return (
              <div key={idx} data-testid={`career-step-${idx}`} className="bg-white rounded-2xl border border-slate-200 p-5 hover:-translate-y-1 hover:shadow-lg transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#00B37E]/12 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#00B37E]" />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">
                    Step 0{idx + 1}
                  </div>
                </div>
                <div className="mt-4 font-heading text-base font-bold text-[#0A192F]">{v.t}</div>
                <div className="text-sm text-[#64748B] mt-1.5 leading-relaxed">{v.d}</div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <form
            onSubmit={submit}
            data-testid="career-form"
            className="bg-white border border-slate-200 rounded-3xl p-7 md:p-10"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#0A192F] flex items-center justify-center">
                <Target className="w-6 h-6 text-[#00B37E]" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-bold text-[#0A192F]">Submit your resume for placement</h3>
                <p className="text-sm text-[#64748B] mt-0.5">TASSEI students &amp; alumni — let our placement cell take it from here.</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cname">Full Name *</Label>
                <Input id="cname" data-testid="career-name" value={form.name} onChange={set("name")} placeholder="Your name" required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="cphone">Phone *</Label>
                <Input id="cphone" data-testid="career-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." required className="mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="cemail">Email *</Label>
                <Input id="cemail" data-testid="career-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="cpos">Preferred Role / Industry *</Label>
                <Input id="cpos" data-testid="career-position" value={form.position} onChange={set("position")} placeholder="e.g. Junior Accountant, Data Analyst, BPO" required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="cexp">TASSEI Course / Experience *</Label>
                <Input id="cexp" data-testid="career-experience" value={form.experience} onChange={set("experience")} placeholder="e.g. Completed ADCA · Fresher" required className="mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="cresume">Resume Link (Google Drive / Dropbox) *</Label>
                <Input id="cresume" data-testid="career-resume" value={form.resume_link} onChange={set("resume_link")} placeholder="https://drive.google.com/..." className="mt-1.5" />
                <p className="text-xs text-[#64748B] mt-1.5">Tip: Upload your resume to Drive, set link sharing to &ldquo;Anyone with the link&rdquo; and paste it here.</p>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="ccover">Anything else we should know?</Label>
                <Textarea id="ccover" data-testid="career-cover" value={form.cover_note} onChange={set("cover_note")} placeholder="Preferred city, salary expectation, strengths, etc." rows={4} className="mt-1.5" />
              </div>
            </div>

            <Button type="submit" disabled={loading} data-testid="career-submit" className="mt-7 bg-[#00B37E] hover:bg-[#009166] text-white font-semibold rounded-full h-12 px-7">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {loading ? "Submitting..." : "Submit Resume"}
            </Button>

            <p className="text-xs text-[#64748B] mt-5">
              Not a TASSEI student yet? <a href="/courses" className="text-[#00B37E] font-semibold hover:underline">Browse our courses</a> first — placement support is included with every program.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
